import React, { useState } from 'react';
import { FileText, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REPORT_TYPES = [
  { value: 'daily-income', label: 'Daily Income', desc: 'Payment approvals and revenue' },
  { value: 'installation', label: 'Installation Performance', desc: 'Service team job records' },
  { value: 'technician', label: 'Technician Productivity', desc: 'Completed job analysis' },
  { value: 'tickets', label: 'Ticket Resolution', desc: 'Support ticket analytics' },
  { value: 'inventory', label: 'Inventory Usage', desc: 'Stock levels and usage' },
  { value: 'hr', label: 'HR Report', desc: 'Employee data overview' },
];

export default function Reports() {
  const [selected, setSelected] = useState('daily-income');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const res = await fetch(`${API}/reports/${selected}`); setReport(await res.json()); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/reports/${selected}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${selected}_report.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const renderData = (data) => {
    if (!Array.isArray(data) || !data.length) return <p className="text-sm text-[var(--md-on-surface-variant)] py-4 text-center">No data</p>;
    const keys = Object.keys(data[0]).filter(k => k !== '_id').slice(0, 7);
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-[var(--md-surface-1)]">
            {keys.map(k => <th key={k} className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-3 py-2 whitespace-nowrap uppercase">{k.replace(/_/g,' ')}</th>)}
          </tr></thead>
          <tbody>
            {data.slice(0, 50).map((row, i) => (
              <tr key={i} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                {keys.map(k => <td key={k} className="px-3 py-2 text-xs whitespace-nowrap">{typeof row[k] === 'object' ? JSON.stringify(row[k]) : String(row[k]??'')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length > 50 && <p className="text-[10px] text-[var(--md-on-surface-variant)] p-2">Showing 50 of {data.length}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {REPORT_TYPES.map(r => (
          <button key={r.value} onClick={() => setSelected(r.value)}
            className={`md3-card p-3 text-left transition-all ${selected === r.value ? 'ring-2 ring-[var(--md-primary)]' : ''}`} data-testid={`report-${r.value}`}>
            <div className="flex items-center gap-2 mb-1">
              <FileText size={16} className={selected === r.value ? 'text-[var(--md-primary)]' : 'text-[var(--md-on-surface-variant)]'} />
              <span className="text-xs font-medium">{r.label}</span>
            </div>
            <p className="text-[10px] text-[var(--md-on-surface-variant)]">{r.desc}</p>
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={generate} disabled={loading} className="md3-filled-btn text-xs" data-testid="gen-report-btn">{loading ? 'Generating...' : 'Generate Report'}</button>
        {report && <button onClick={exportPDF} className="md3-outlined-btn text-xs flex items-center gap-1" data-testid="export-pdf-btn"><DownloadSimple size={14} /> Export PDF</button>}
      </div>

      {report && (
        <div className="md3-card overflow-hidden" data-testid="report-content">
          <div className="px-4 py-3 bg-[var(--md-surface-1)] border-b border-[var(--md-outline-variant)] flex justify-between">
            <div>
              <h3 className="text-sm font-medium">{REPORT_TYPES.find(r=>r.value===report.type)?.label}</h3>
              <p className="text-[10px] text-[var(--md-on-surface-variant)]">Generated: {new Date(report.generated_at).toLocaleString()}</p>
            </div>
          </div>
          {renderData(report.data)}
        </div>
      )}
    </div>
  );
}
